import { ITEM_LOOKS, type ItemShape } from "@eldrun/content";
import type { BufferGeometry } from "three";
import { ITEM_MODELS, MODELS, type ModelId } from "../assets/models.generated.ts";
import { itemGeometry } from "./item-meshes.ts";
import type { ModelLibrary } from "./model-library.ts";

/**
 * Modelos dos itens (F47): ferramentas, armas, flechas e materiais de `ITEM_LOOKS` vêm do pacote
 * `itens` (KayKit, punho na origem), com as partes de metal na cor do tier numa vista que divide os
 * buffers do modelo (`ModelLibrary.tinted`). LOD 0 na mão e no ícone, LOD 1 no chão. O resto do
 * catálogo (e tudo, se o pacote faltar) usa a malha procedural de `item-meshes.ts`.
 */

/** Formas lidas de outro pacote pelo id, pintadas inteiras: a pedra é a pedrinha dos enfeites do chão (F44). */
export const SHARED_ITEM_MODELS: Readonly<Partial<Record<ItemShape, ModelId>>> = { pedra: "pedrinha" };

/** Modelo da forma: o do pacote `itens` ou o de outro pacote. */
export const itemShapeModel = (shape: ItemShape): ModelId | undefined => ITEM_MODELS[shape] ?? SHARED_ITEM_MODELS[shape];

/** LOD dos itens: 0 na mão e no ícone, 1 no chão. */
export type ItemLod = 0 | 1;

/**
 * Geometria do modelo do item no LOD, com a cor do tier; `undefined` se o item é procedural ou o
 * modelo não carregou. O modelo sem o LOD pedido (a pedrinha só tem um) usa o último que tiver.
 */
export function itemModel(itemId: string, lod: ItemLod, library: ModelLibrary): BufferGeometry | undefined {
  const look = ITEM_LOOKS[itemId];
  const id = look && itemShapeModel(look.shape);
  if (!look || !id) return undefined;
  const level = Math.min(lod, MODELS[id].tris.length - 1);
  if (!look.tint) return library.get(id, level);
  return library.tinted(id, level, look.tint, !ITEM_MODELS[look.shape]);
}

/** Malha do item: o modelo (`itemModel`) ou a procedural de reserva. */
export function itemMesh(itemId: string, lod: ItemLod, library: ModelLibrary): BufferGeometry {
  return itemModel(itemId, lod, library) ?? itemGeometry(itemId);
}
